import { useEffect, useState } from "react";
import { Bell, Truck, Sparkles, Package, Check, X, ZoomIn } from "lucide-react";
import { Reveal, Eyebrow } from "./Reveal";
import { scrollToId } from "../lib/scroll";

const PRODUCTS = [
  {
    name: "Mi Binky Multipacks",
    desc: "The only way to buy Mi Binky at launch — twin packs and the complete bundle, in every colour.",
    img: "/assets/multipacks-discovery.webp",
    imgClass: "h-full w-full object-contain p-2",
    alt: "Mi Binky Comfort Drops Collection — Discovery Collection four-bottle pack together with the Decongestant and Nap Time two-packs",
    tag: "Bundles",
    note: "Available at launch",
    badge: "Launch Exclusive",
    featured: true,
    zoom: "discovery",
    options: [
      "Twin Pack — your choice of comfort drops: Decongestant, Eucalyptus, Chamomile or Nap & Night-Time",
      "Complete Multipack — all four comfort drops fragrances in one bundle",
    ],
  },
  {
    name: "Mi Binky Comfort System Pack",
    desc: "The complete kit — comfort pacifier, sterile cotton inserts, 15 ml comfort drops, precision tweezers and step-by-step guide.",
    img: "/assets/binky-pack-pink.png",
    imgClass: "max-h-full w-auto object-contain p-5",
    alt: "Mi Binky Comfort System retail pack — pink pacifier",
    tag: "Flagship kit",
    note: "Pink · Blue · Red · Yellow",
    badge: "Coming Soon",
    availability: "Multipack only at launch",
  },
  {
    name: "Cotton Strip Refills",
    desc: "Sterile, individually wrapped 100% cotton inserts — sized precisely for the Mi Binky comfort chamber.",
    img: "/assets/cotton-front.png",
    imgClass: "max-h-full w-auto object-contain p-5",
    alt: "BINKY.LIFE sterile cotton strip refill pack",
    tag: "Refills",
    note: "30 sterile refills per pack",
    badge: "Coming Soon",
    availability: "Multipack only at launch",
  },
  {
    name: "15 ml Comfort Range",
    desc: "Decongestant, Eucalyptus, Chamomile and Nap & Night-Time — soft squeeze bottles made for the cotton insert.",
    img: "/assets/bottles-group.png",
    imgClass: "h-full w-full object-cover object-center",
    alt: "The four BINKY.LIFE 15 ml comfort bottles together",
    tag: "Comfort drops",
    note: "Four fragrances · 15 ml",
    badge: "Coming Soon",
    availability: "Multipack only at launch",
  },
];

const ZOOM_IMAGES = {
  discovery: {
    src: "/assets/multipacks-discovery.webp",
    alt: "Mi Binky Comfort Drops Collection — Discovery Collection four-bottle pack together with the Decongestant and Nap Time two-packs",
  },
  range: {
    src: "/assets/two-pack-range.webp",
    alt: "The complete Mi Binky two-pack range — Decongestant, Chamomile, Nap Time and Eucalyptus, each containing two 15 ml bottles",
  },
};

export const Shop = () => {
  const [zoom, setZoom] = useState(null);

  useEffect(() => {
    if (!zoom) return;
    const onKey = (e) => e.key === "Escape" && setZoom(null);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoom]);

  return (
    <section id="shop" data-testid="shop-section" className="relative overflow-hidden py-24 sm:py-32">
      <div className="pointer-events-none absolute -left-40 top-40 h-[420px] w-[420px] rounded-full bg-binky-blue/10 blur-[110px]" />
      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <Eyebrow>Shop — Coming Soon</Eyebrow>
          <h2 className="mt-4 font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">
            The Mi Binky shop <span className="text-binky-pink">opens soon.</span>
          </h2>
          <p className="mt-4 text-base leading-relaxed text-slate-600 md:text-lg">
            At launch, every Mi Binky product is available inside our multipacks — individual packs,
            refills and bottles follow shortly after. Prices and delivery charges will be confirmed
            before we open, so register your interest and you&apos;ll be the first to know.
          </p>
        </Reveal>

        <div className="mt-14 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {PRODUCTS.map((p, i) => (
            <Reveal
              key={p.name}
              delay={i * 0.08}
              className={`group flex h-full flex-col overflow-hidden rounded-[28px] border bg-white transition-all duration-500 hover:-translate-y-1.5 ${
                p.featured
                  ? "border-binky-pink/40 shadow-[0_24px_60px_-20px_rgba(240,62,155,0.45)] ring-2 ring-binky-pink/60 hover:shadow-[0_36px_72px_-24px_rgba(240,62,155,0.5)]"
                  : "border-slate-100 shadow-[0_20px_50px_-24px_rgba(27,79,160,0.25)] hover:shadow-[0_32px_64px_-24px_rgba(240,62,155,0.3)]"
              }`}
              data-testid={`shop-card-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
            >
              <div className="relative flex h-60 items-center justify-center overflow-hidden bg-gradient-to-b from-slate-50 to-white">
                {p.zoom ? (
                  <button
                    data-testid="shop-multipacks-image-button"
                    onClick={() => setZoom(p.zoom)}
                    className="relative flex h-full w-full cursor-zoom-in items-center justify-center"
                    aria-label="View the Mi Binky multipacks image full-size"
                  >
                    <img
                      src={p.img}
                      alt={p.alt}
                      loading="lazy"
                      data-testid="shop-multipacks-image"
                      className={`${p.imgClass} drop-shadow-lg transition-transform duration-700 group-hover:scale-105`}
                    />
                    <span className="absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-full bg-binky-navy/85 px-3 py-1.5 text-[10px] font-bold text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
                      <ZoomIn className="h-3 w-3" />
                      View full-size
                    </span>
                  </button>
                ) : (
                  <img
                    src={p.img}
                    alt={p.alt}
                    loading="lazy"
                    className={`${p.imgClass} drop-shadow-lg transition-transform duration-700 group-hover:scale-105`}
                  />
                )}
                <span
                  className={`absolute left-4 top-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.14em] text-white shadow-lg ${
                    p.featured
                      ? "bg-binky-green shadow-binky-green/30"
                      : "bg-binky-pink shadow-binky-pink/30"
                  }`}
                >
                  {p.featured ? <Package className="h-3 w-3" /> : <Sparkles className="h-3 w-3" />}
                  {p.badge}
                </span>
              </div>
              <div className="flex flex-1 flex-col p-6">
                <h3 className="font-display text-lg font-bold text-slate-900">{p.name}</h3>
                <span className="mt-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-binky-blue/60">
                  {p.tag} · {p.note}
                </span>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-600">{p.desc}</p>
                {p.options && (
                  <ul className="mt-4 space-y-2.5" data-testid="multipack-options">
                    {p.options.map((o) => (
                      <li key={o} className="flex items-start gap-2.5 text-[13px] font-semibold leading-snug text-slate-700">
                        <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-binky-green/15 text-binky-green">
                          <Check className="h-3 w-3" />
                        </span>
                        {o}
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-4 text-xs font-bold uppercase tracking-[0.14em] text-slate-400">
                  Price TBA — announced at launch
                </p>
                {p.availability && (
                  <p
                    data-testid={`shop-availability-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="mt-1.5 inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-[0.12em] text-binky-blue"
                  >
                    <Package className="h-3.5 w-3.5" />
                    {p.availability}
                  </p>
                )}
                <button
                  data-testid={`shop-notify-button-${p.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  onClick={() => scrollToId("#register")}
                  className={`mt-3 inline-flex items-center justify-center gap-2 rounded-full px-5 py-3 text-sm font-bold transition-all duration-300 active:scale-95 ${
                    p.featured
                      ? "bg-binky-pink text-white shadow-lg shadow-binky-pink/30 hover:scale-[1.03]"
                      : "border-2 border-binky-pink/25 bg-white text-binky-pink hover:border-binky-pink hover:bg-binky-pink hover:text-white"
                  }`}
                >
                  <Bell className="h-4 w-4" />
                  Notify Me at Launch
                </button>
              </div>
            </Reveal>
          ))}
        </div>

        <Reveal className="mt-14" data-testid="two-pack-range-block">
          <div className="text-center">
            <h3 className="font-display text-2xl font-extrabold tracking-tight text-slate-900 sm:text-3xl" data-testid="two-pack-range-heading">
              The Complete Mi Binky <span className="text-binky-pink">Two-Pack Range</span>
            </h3>
            <p className="mx-auto mt-3 max-w-xl text-sm leading-relaxed text-slate-600 md:text-base" data-testid="two-pack-range-subtext">
              Choose from Decongestant, Chamomile, Nap Time or Eucalyptus — each containing two 15 ml
              bottles.
            </p>
          </div>
          <button
            data-testid="two-pack-range-image-button"
            onClick={() => setZoom("range")}
            className="group relative mx-auto mt-8 block w-full max-w-3xl cursor-zoom-in overflow-hidden rounded-[28px] border border-slate-100 bg-white shadow-[0_24px_60px_-24px_rgba(27,79,160,0.3)] transition-all duration-500 hover:-translate-y-1 hover:shadow-[0_36px_72px_-24px_rgba(240,62,155,0.35)]"
            aria-label="View the complete Mi Binky two-pack range image full-size"
          >
            <img
              src="/assets/two-pack-range.webp"
              alt="The complete Mi Binky two-pack range — Decongestant, Chamomile, Nap Time and Eucalyptus, each containing two 15 ml bottles"
              loading="lazy"
              data-testid="two-pack-range-image"
              className="h-auto w-full object-contain"
            />
            <span className="absolute bottom-4 right-4 inline-flex items-center gap-2 rounded-full bg-binky-navy/85 px-4 py-2 text-xs font-bold text-white opacity-0 backdrop-blur transition-opacity duration-300 group-hover:opacity-100">
              <ZoomIn className="h-3.5 w-3.5" />
              Tap to view full-size
            </span>
          </button>
        </Reveal>

        <Reveal className="mt-10" data-testid="shop-delivery-note">
          <div className="flex flex-col items-center justify-center gap-3 rounded-[28px] border border-binky-blue/10 bg-sky-50/70 px-8 py-6 text-center sm:flex-row sm:gap-4">
            <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-binky-blue/10 text-binky-blue">
              <Truck className="h-5 w-5" />
            </span>
            <p className="text-sm leading-relaxed text-slate-600">
              <strong className="font-bold text-slate-900">Delivery:</strong> options and charges —
              including UK and international shipping — will be confirmed before the shop opens.
            </p>
          </div>
        </Reveal>
      </div>

      {zoom && (
        <div
          data-testid="shop-image-lightbox"
          className="fixed inset-0 z-[100] flex items-center justify-center bg-binky-navy/90 p-4 backdrop-blur-md sm:p-8"
          onClick={() => setZoom(null)}
          role="dialog"
          aria-modal="true"
          aria-label="Product image — full-size view"
        >
          <button
            data-testid="shop-image-lightbox-close"
            onClick={() => setZoom(null)}
            className="absolute right-5 top-5 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 text-white backdrop-blur transition-colors hover:bg-binky-pink"
            aria-label="Close full-size image"
          >
            <X className="h-6 w-6" />
          </button>
          <img
            src={ZOOM_IMAGES[zoom].src}
            alt={ZOOM_IMAGES[zoom].alt}
            data-testid="shop-image-lightbox-image"
            className="max-h-[92vh] max-w-[94vw] rounded-2xl object-contain shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </section>
  );
};
